import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { locationColumns } from "../../utils/table/locationPartyCols";
import CustomTable from "../common/CustomTable";
import { addLocation, editLocation, getLocation } from "../../api/inventory";
import MessageContainer from "../common/MessageContainer";
import { Fragment, useEffect, useState } from "react";
import { LocationPartyEntryDetails } from "../../interfaces/app_types";
import { useForm } from "react-hook-form";
import { useRecoilValue } from "recoil";
import {
  locationPartyEditIdState,
  locationPartyEditState,
} from "../../utils/atoms/atoms";
import FormField from "../common/FormField";
import { locationValidationSchema } from "../../utils/validations/validations";
import { yupResolver } from "@hookform/resolvers/yup";

const LocationModalBody = () => {
  let label = "";
  let color = "";
  const resolver = yupResolver(locationValidationSchema);

  const [addBtnName, setAddBtnName] = useState("Add");

  const {
    isLoading,
    data: location,
    isSuccess,
    isError,
  } = useQuery({
    queryKey: ["location"],
    queryFn: getLocation,
  });
  const defaultValues = {
    label: label || "",
    color: color || "#000000",
  };
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<LocationPartyEntryDetails>({
    defaultValues,
    resolver,
  });

  const [disabledField, setDisabledField] = useState(true);
  const id = useRecoilValue(locationPartyEditIdState);
  const editState = useRecoilValue(locationPartyEditState);

  const resetFields = () => {
    reset({
      label: label || "",
      color: color || "#000000",
    });
  };

  useEffect(() => {
    if (editState === "location") {
      const selectedLocation = location.filter((loc) => loc.value === id)[0];
      label = selectedLocation.label;
      color = selectedLocation.color;
      resetFields();
      setDisabledField(false);
      setAddBtnName("Edit");
    }
  }, [id, editState]);

  const queryClient = useQueryClient();

  const editLocationMutation = useMutation({
    mutationFn: async (data: any) => {
      return editLocation(id, data);
    },
    onSuccess() {
      queryClient.invalidateQueries(["location"]);

      queryClient.invalidateQueries(["inventory"]);

      setDisabledField(true);
      setAddBtnName("Add");
      resetFields();
    },
  });

  const addLocationMutation = useMutation({
    mutationFn: async (data: any) => {
      return addLocation(data);
    },
    onSuccess() {
      queryClient.invalidateQueries(["location"]);

      queryClient.invalidateQueries(["inventory"]);

      setDisabledField(true);
      resetFields();
    },
  });

  return isLoading ? (
    <MessageContainer message={"Loading"} />
  ) : isSuccess && !isError ? (
    <Fragment>
      <form
        action=""
        onSubmit={handleSubmit((data, event) => {
          event?.preventDefault();

          if (addBtnName === "Edit") {
            editLocationMutation.mutate(data);
          } else {
            addLocationMutation.mutate(data);
          }
        })}
      >
        <div className="d-flex gap-1 justify-content-between">
          <div className="d-flex col-8 flex-wrap">
            {addBtnName === "Edit" ? (
              <FormField
                title="ID"
                value={id}
                fieldType="display"
                disabled={true}
              />
            ) : (
              ""
            )}
            <FormField
              title="Name"
              value={label}
              disabled={disabledField}
              errorMessage={errors?.label?.message}
              formRegister={{ ...register("label") }}
            />
            <FormField
              title="Color"
              value={color}
              fieldType="color"
              disabled={disabledField}
              errorMessage={errors?.color?.message}
              formRegister={{ ...register("color") }}
            />
          </div>
          <div className="d-flex align-items-center flex-wrap">
            <button
              className="styled-btn--std me-2"
              disabled={!disabledField}
              onClick={() => {
                setDisabledField(false);
              }}
            >
              {addBtnName}
            </button>
            <button
              className="styled-btn--std me-2"
              type="submit"
              disabled={disabledField}
            >
              Submit
            </button>
            <button
              className="styled-btn--std me-2"
              disabled={disabledField}
              onClick={() => {
                setDisabledField(true);
                setAddBtnName("Add");
                resetFields();
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
      <CustomTable data={location} columns={locationColumns} />
    </Fragment>
  ) : (
    ""
  );
};

export default LocationModalBody;
